import { Formik } from "formik";
import React, { useRef } from "react";

import AppConfig from "../../AppConfig";
import { updateEventDetails } from "../http/events";
import { EventDetailsSchema } from "../screens/Schemas";
import AutoSave from "./AutoSave";
import { TextField } from "@/components/settings/Field";
import LinksEditor from "@/components/settings/LinksEditor";
import { SettingsSection } from "@/components/settings/SettingsPage";
import { t } from "@/localization";

// Title, location and the links/files under the video. Saves automatically.
// `middle` renders between the title and the links (the date and event link
// sections, which have their own forms).
const EventDetailsForm = ({ event, prefer_multiple_tests, middle }) => {
  const {
    slug,
    title,
    event_type,
    event_location,
    links,
    is_test,
    broadcaster,
  } = event;

  // Last saved title, so a fundraiser name sync only runs on real title changes.
  const savedTitle = useRef(title);
  const titleLocked = !!is_test && !prefer_multiple_tests;

  return (
    <Formik
      initialValues={{
        title,
        event_type: event_type || "wedding",
        event_location: event_location || "",
        links: links || [],
      }}
      onSubmit={async (values) => {
        // Errors propagate to AutoSave, which shows "Couldn't save · Retry".
        const saved = await updateEventDetails(
          { slug, ...values },
          { previousTitle: savedTitle.current }
        );
        savedTitle.current = saved?.title ?? values.title;
      }}
      validationSchema={EventDetailsSchema}
    >
      {(f) => (
        <>
          <AutoSave {...f} />
          <SettingsSection first title={t("Title and location")}>
            <TextField
              label={t("Event title")}
              help="details.title"
              value={f.values.title}
              onChangeText={f.handleChange("title")}
              onBlur={f.handleBlur("title")}
              placeholder={t("e.g. Anna & Mark's wedding")}
              disabled={titleLocked}
              hint={titleLocked ? t("Test events keep their name.") : undefined}
              error={f.touched.title ? f.errors.title : null}
            />
            <TextField
              label={t("Location")}
              optional
              help="details.location"
              value={f.values.event_location}
              onChangeText={f.handleChange("event_location")}
              onBlur={f.handleBlur("event_location")}
              placeholder={t("e.g. St. Mary's Church, Austin")}
              error={f.touched.event_location ? f.errors.event_location : null}
              last
            />
          </SettingsSection>

          {middle}

          <SettingsSection
            title={t("Links and files")}
            description={t(
              "Shown under the video on your event page: a registry, a donation page, a website or a program."
            )}
          >
            <LinksEditor
              links={f.values.links}
              onChange={(next) => f.setFieldValue("links", next)}
              uploadPath={`${broadcaster}/${slug}`}
              bucket={AppConfig.REACT_APP_FILEUPLOAD_BUCKET}
            />
          </SettingsSection>
        </>
      )}
    </Formik>
  );
};
export default EventDetailsForm;
